import { SORT_ICON_ASC, SORT_ICON_DESC, SORT_ICON_NONE } from '@appViews/shared/sortIcon';
import { uiLocale } from '@boViews/shared/LanguageTabs/languageName';
import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import Button from '@sharedComponents/Button';
import FlexBox from '@sharedComponents/FlexBox';
import Table from '@sharedComponents/Table';
import Tag from '@sharedComponents/Tag';
import Typography from '@sharedComponents/Typography';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { languageLabel, SORTABLE_COLUMNS, sortTestsBy, testTitle } from '../testRules';
import TestStatusBadge from '../TestStatusBadge';

import * as style from './style.module.css';

const EMPTY = '—';
const noop = () => {};

/**
 * Table of the tests of a collection: the reference, the title with the level below it and a badge on a pedagogical
 * test, the language, the status and a bin (`onRemove(test)`). The values are read only; a missing one shows as a
 * dash. The rows are sorted by reference, or by the column whose header was clicked, a second click reversing the
 * order (see sortTestsBy); the sort outlasts additions and removals. `languagesOptions` are the catalogue languages
 * (their id labelled by their code). A reference of `pendingReferences` disables the bin of its row. Once the test
 * of the last activated bin leaves `tests`, the focus that fell with its row moves to the bin now at its place, or,
 * when no test is left, to the caller through `onEmptied()`. The table sits in a frame and scrolls inside it when its
 * columns do not fit. Without a test, a message replaces the table.
 */
const TestTable = ({ tests = [], languagesOptions = [], onRemove = noop, onEmptied = noop, pendingReferences = [] }) => {
    const { t, i18n } = useTranslation('bo');
    const locale = uiLocale(i18n);
    const frameRef = useRef(null);
    // Reference and displayed index of the test of the last activated bin.
    const removalRef = useRef(null);
    // Column the rows are sorted by, prefixed with "-" for the descending order.
    const [sortBy, setSortBy] = useState('reference');

    useEffect(() => {
        const removal = removalRef.current;
        if (!removal || tests.some((test) => test.reference === removal.reference)) return;
        removalRef.current = null;
        // The focus is moved only when it fell with the row, not when the user moved it meanwhile.
        if (document.activeElement && document.activeElement !== document.body) return;
        if (tests.length === 0) {
            onEmptied();
            return;
        }
        frameRef.current?.querySelector(`[data-remove-index="${Math.min(removal.index, tests.length - 1)}"]`)?.focus();
    }, [tests, onEmptied]);

    if (tests.length === 0) {
        return (
            <Typography color="neutral-secondary">{t('No test. Search for a test by its reference or its title to add it.')}</Typography>
        );
    }

    const nameCell = (test) => {
        const title = testTitle(test);
        return (
            <FlexBox flexDirection="column" gap="xxs">
                {title ? (
                    <Typography fontSize="s">{title}</Typography>
                ) : (
                    <Typography fontSize="s" color="warning-primary">
                        {t('Missing title')}
                    </Typography>
                )}
                <FlexBox alignItems="center" gap="xs" flexWrap="wrap">
                    <Typography span fontSize="xs" color="neutral-secondary">
                        {test.level?.name ?? EMPTY}
                    </Typography>
                    {test.pedagogical && (
                        <Tag color="lavender" size="xs" fontSize="xs">
                            {t('Pedagogical')}
                        </Tag>
                    )}
                </FlexBox>
            </FlexBox>
        );
    };

    const shownTests = sortTestsBy(tests, sortBy, { languagesOptions, locale });
    const indexes = new Map(shownTests.map((test, index) => [test.reference, index]));

    // A first click on a column sorts by it in ascending order, a second click reverses the order.
    const sort = (column) => {
        if (!SORTABLE_COLUMNS.includes(column)) return;
        setSortBy((current) => {
            if (current.replace(/^-/, '') !== column) return column;
            return current.startsWith('-') ? column : `-${column}`;
        });
    };
    const sortIconOf = (column) => {
        if (sortBy.replace(/^-/, '') !== column) return SORT_ICON_NONE;
        return sortBy.startsWith('-') ? SORT_ICON_DESC : SORT_ICON_ASC;
    };

    const removeCell = (test) => {
        const label = t('Remove {{reference}} from the collection', { reference: test.reference });
        const index = indexes.get(test.reference);
        const remove = () => {
            removalRef.current = { reference: test.reference, index };
            return onRemove(test);
        };
        return (
            <Button
                ghost
                icon={faTrash}
                title={label}
                aria-label={label}
                data-remove-index={index}
                disabled={pendingReferences.includes(test.reference)}
                action={remove}
            />
        );
    };

    // Classes of the header and body cells of a column, `width` giving its width.
    const cellClasses = (width) => ({ thClassName: `${style.head} ${width}`, tdClassName: `${style.cell} ${width}` });

    const columns = [
        {
            key: 'reference',
            label: t('Reference'),
            icon: sortIconOf('reference'),
            pinned: true,
            ...cellClasses(style.referenceCol),
            render: (test) => (
                <Typography fontSize="s" className={style.reference}>
                    {test.reference}
                </Typography>
            ),
        },
        {
            key: 'name',
            label: t('Name / Level'),
            icon: sortIconOf('name'),
            pinned: true,
            fill: true,
            ...cellClasses(style.nameCol),
            render: nameCell,
        },
        {
            key: 'language',
            label: t('Language'),
            icon: sortIconOf('language'),
            pinned: true,
            ...cellClasses(style.languageCol),
            render: (test) => <Typography fontSize="s">{languageLabel(languagesOptions, test.languageId, locale) ?? EMPTY}</Typography>,
        },
        {
            key: 'status',
            label: t('Status'),
            icon: sortIconOf('status'),
            pinned: true,
            ...cellClasses(style.statusCol),
            render: (test) => (test.status ? <TestStatusBadge status={test.status} /> : <Typography fontSize="s">{EMPTY}</Typography>),
        },
        { key: 'remove', ariaLabel: t('Remove'), pinned: true, ...cellClasses(style.removeCol), render: removeCell },
    ];

    return (
        <FlexBox ref={frameRef} flexDirection="column" className={style.frame}>
            <Table columns={columns} items={shownTests} getRowKey={(test) => test.reference} onSort={sort} />
        </FlexBox>
    );
};

export default TestTable;
